import React from 'react';
import { Card } from '@/components/ui/card';
import { usePublicActivity, PublicActivity } from '@/hooks/useAdminLogs';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  UserPlus, 
  Users, 
  Calendar, 
  Briefcase, 
  GraduationCap,
  Activity
} from 'lucide-react';

const getActivityIcon = (type: PublicActivity['type']) => {
  switch (type) {
    case 'new_member':
      return <UserPlus className="h-4 w-4 text-green-500" />;
    case 'new_connection':
      return <Users className="h-4 w-4 text-blue-500" />;
    case 'new_event':
      return <Calendar className="h-4 w-4 text-purple-500" />;
    case 'new_opportunity':
      return <Briefcase className="h-4 w-4 text-orange-500" />;
    case 'new_workshop':
      return <GraduationCap className="h-4 w-4 text-indigo-500" />;
    default:
      return <Activity className="h-4 w-4 text-gray-400" />;
  }
};

const RecentActivity = () => {
  const { activities, isLoading, error } = usePublicActivity();

  if (error) {
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 text-foreground">Activité de la Communauté</h3>
        <p className="text-muted-foreground">Impossible de charger l'activité récente</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-4 text-foreground">Activité de la Communauté</h3>
      
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
              <div className="flex-1 space-y-1">
                <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                <div className="h-3 bg-muted rounded animate-pulse w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : activities.length === 0 ? (
        <div className="text-center py-8">
          <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Aucune activité récente dans la communauté</p>
        </div>
      ) : (
        <div className="space-y-4">
          {activities.map((activity) => (
            <div key={activity.id} className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center">
                {getActivityIcon(activity.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {activity.title}
                </p>
                <p className="text-sm text-muted-foreground mb-1">
                  {activity.description}
                </p>
                <div className="text-xs text-muted-foreground">
                  {format(new Date(activity.created_at), 'dd MMM à HH:mm', { locale: fr })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default RecentActivity;